/* Error boundary — catches React render errors to prevent white screens. */
import { Component, Fragment } from "react";

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    // `resetKey` is bumped by Try Again and used as the children's `key`,
    // which forces React to unmount and remount the subtree. Clearing
    // `hasError` alone re-rendered the *same* component instances with the
    // same state and no effects re-run, so a crash caused by bad state
    // reproduced instantly and the button looked broken.
    // (features-incomplete audit F51)
    this.state = { hasError: false, error: null, errorInfo: null, resetKey: 0 };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-lg text-center">
            <h2 className="text-xl font-bold text-red-600 mb-2">Something went wrong</h2>
            <p className="text-gray-600 text-sm mb-2">{this.state.error?.message || "An unexpected error occurred."}</p>
            {this.state.error?.stack && (
              <pre className="text-left text-xs text-gray-400 bg-gray-100 p-3 rounded mb-3 max-h-40 overflow-auto whitespace-pre-wrap">{this.state.error.stack}</pre>
            )}
            {this.state.errorInfo?.componentStack && (
              <pre className="text-left text-xs text-gray-400 bg-gray-100 p-3 rounded mb-3 max-h-40 overflow-auto whitespace-pre-wrap">{this.state.errorInfo.componentStack}</pre>
            )}
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => this.setState((s) => ({
                  hasError: false, error: null, errorInfo: null, resetKey: s.resetKey + 1,
                }))}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300">
                Try Again
              </button>
              <button
                onClick={() => { this.setState({ hasError: false }); window.location.href = "/"; }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
                Return to Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }
    return <Fragment key={this.state.resetKey}>{this.props.children}</Fragment>;
  }
}
